import React, { FC, useEffect, useRef } from 'react'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-decomposition'
import type { HighlightSpan } from './text-highlight'
import { getClassNames } from '../../_utils/classnames'
import './brand-voice-decomposition.scss'

interface IHighlightedTextArea {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    spans: HighlightSpan[]
    /** The currently "focused" span — from hovering a rule row, or from the caret sitting
     * inside a matched range in the textarea itself. Emphasized in the overlay; every other
     * matched span dims slightly so the active one stands out. */
    activeSpanKey: string | null
    onSpanHover: (key: string | null) => void
    /** Set (with a fresh `nonce`) to scroll the span with this key into view — a click on a
     * rule row, not a hover, since scrolling the author's own view out from under them on a
     * mere hover would be disorienting. `nonce` lets the same key be requested twice in a row
     * (e.g. after the author has since scrolled away) and still re-trigger the effect below,
     * which a bare key wouldn't since it wouldn't look like a change. */
    scrollToSpan?: { key: string; nonce: number } | null
}

const TIER_MARK_BACKGROUND: Record<HighlightSpan['tier'], string> = {
    checked: `color-mix(in srgb, ${BRAND_VOICE_TIER_VISUAL.checked.color}, transparent 75%)`,
    needsReview: `color-mix(in srgb, ${BRAND_VOICE_TIER_VISUAL.needsReview.color}, transparent 75%)`,
}

/** Appends `text` into `container`, turning each `\n` into a real `<br>` — a plain text node
 * containing "\n" has no visual effect in a contentEditable element (unlike a native
 * `<textarea>`, which renders "\n" as a line break natively). Shared by both the plain-text
 * segments and the inside of each `<mark>`, so a quote that happens to span a paragraph break
 * still renders correctly. */
function appendTextWithBreaks(container: Node, text: string) {
    const lines = text.split('\n')
    lines.forEach((line, i) => {
        if (line) container.appendChild(document.createTextNode(line))
        if (i < lines.length - 1) container.appendChild(document.createElement('br'))
    })
}

/** Rebuilds `root`'s children from scratch to match `value`/`spans`/`activeSpanKey` — the
 * single source of truth for what's rendered, imperative on purpose (see the component
 * docstring for why this never goes through React's own reconciliation). */
function renderContent(root: HTMLElement, value: string, spans: HighlightSpan[], activeSpanKey: string | null) {
    root.innerHTML = ''
    let cursor = 0

    spans.forEach((span) => {
        const start = Math.max(span.start, cursor)
        if (start >= span.end) return // fully consumed by an earlier overlapping span

        if (start > cursor) appendTextWithBreaks(root, value.slice(cursor, start))

        const isDimmed = activeSpanKey !== null && activeSpanKey !== span.key
        const isActive = activeSpanKey === span.key
        const mark = document.createElement('mark')
        mark.dataset.spanKey = span.key
        Object.assign(mark.style, {
            backgroundColor: TIER_MARK_BACKGROUND[span.tier],
            opacity: isDimmed ? '0.35' : '1',
            borderRadius: '3px',
            boxShadow: isActive ? `inset 0 0 0 1px ${BRAND_VOICE_TIER_VISUAL[span.tier].color}` : '',
            transition: 'opacity 120ms ease-in-out',
        })
        appendTextWithBreaks(mark, value.slice(start, span.end))
        root.appendChild(mark)
        cursor = span.end
    })

    if (cursor < value.length) appendTextWithBreaks(root, value.slice(cursor))
}

/** Reads `root`'s children back into the plain-text string they represent — the exact inverse
 * of `renderContent`'s node shapes (text nodes verbatim, `<br>` as "\n", anything else recursed
 * into). This is what a keystroke's `input` event turns back into the controlled `value`. */
function serializePlainText(root: HTMLElement): string {
    let text = ''
    function walk(node: Node) {
        if (node.nodeType === Node.TEXT_NODE) {
            text += (node as Text).data
        } else if (node.nodeName === 'BR') {
            text += '\n'
        } else {
            node.childNodes.forEach(walk)
        }
    }
    root.childNodes.forEach(walk)
    return text
}

/** Plain-text character offset of a DOM (node, offset) position within `root` — the inverse of
 * `getPositionAtOffset`. Used to capture the caret's position (as a plain-text index, immune to
 * whatever nodes happen to exist) before a re-render, so it can be restored after. */
function getPlainTextOffset(root: HTMLElement, targetNode: Node, targetOffset: number): number {
    let total = 0

    function nodeLength(node: Node): number {
        if (node.nodeType === Node.TEXT_NODE) return (node as Text).data.length
        if (node.nodeName === 'BR') return 1
        let len = 0
        node.childNodes.forEach((c) => (len += nodeLength(c)))
        return len
    }

    function walk(node: Node): boolean {
        if (node === targetNode) {
            if (node.nodeType === Node.TEXT_NODE) {
                total += targetOffset
            } else {
                // targetOffset is a CHILD INDEX here (Selection semantics for an element
                // container), not a character offset — sum only the preceding children.
                const children = Array.from(node.childNodes)
                for (let i = 0; i < targetOffset && i < children.length; i++) total += nodeLength(children[i])
            }
            return true
        }
        if (node.nodeType === Node.TEXT_NODE) {
            total += (node as Text).data.length
            return false
        }
        if (node.nodeName === 'BR') {
            total += 1
            return false
        }
        for (const child of Array.from(node.childNodes)) {
            if (walk(child)) return true
        }
        return false
    }

    walk(root)
    return total
}

/** The DOM (node, offset) position `targetOffset` plain-text characters into `root` — the
 * inverse of `getPlainTextOffset`, for restoring a caret position after a re-render. */
function getPositionAtOffset(root: HTMLElement, targetOffset: number): { node: Node; offset: number } {
    let remaining = targetOffset
    let result: { node: Node; offset: number } | null = null

    function walk(node: Node): boolean {
        if (node.nodeType === Node.TEXT_NODE) {
            const len = (node as Text).data.length
            if (remaining <= len) {
                result = { node, offset: remaining }
                return true
            }
            remaining -= len
            return false
        }
        if (node.nodeName === 'BR') {
            if (remaining <= 1) {
                const parent = node.parentNode as Node
                result = { node: parent, offset: Array.from(parent.childNodes).indexOf(node as ChildNode) + 1 }
                return true
            }
            remaining -= 1
            return false
        }
        for (const child of Array.from(node.childNodes)) {
            if (walk(child)) return true
        }
        return false
    }

    if (!walk(root)) {
        // Past the end of the content — collapse to the very end of the editor.
        result = { node: root, offset: root.childNodes.length }
    }
    return result as { node: Node; offset: number }
}

/**
 * A single editable surface with real, in-place `<mark>` highlights — replaces an earlier
 * two-layer design (a real `<textarea>` stacked on a pixel-mirrored backdrop `<div>` drawing
 * the marks underneath it). That design needed the two layers to wrap text at EXACTLY the same
 * points and stay scroll-synced at every instant to look right, and in practice that broke in
 * several distinct ways (scrollbar-gutter mismatch, un-normalized line endings, a stale-compile
 * race, a collapsed-height scroll desync) across repeated real-world testing — each was fixed,
 * but the underlying two-layer premise kept producing a new way to violate it. Rendering the
 * marks as actual children of the ONE element the author is looking at removes the whole
 * category: a mark is exactly where its text is, because it's rendered in the same place as
 * that text, not recomputed against a separate copy.
 *
 * The children of the contentEditable root are NEVER handed to React as JSX — `renderContent`
 * builds them with raw DOM calls, imperatively, only when `spans`/`activeSpanKey` change or
 * `value` changes for a reason other than the author's own last keystroke (see `syncFromProps`
 * below). Letting React reconcile contentEditable's children on every keystroke is a well-known
 * way to fight the browser's own live edits and lose the caret; going fully imperative avoids
 * that class of bug entirely, at the cost of doing the DOM diffing (here, a full rebuild) and
 * caret save/restore by hand.
 */
export const HighlightedTextArea: FC<IHighlightedTextArea> = ({
    value,
    onChange,
    placeholder,
    spans,
    activeSpanKey,
    onSpanHover,
    scrollToSpan,
}) => {
    const editorRef = useRef<HTMLDivElement>(null)
    // What the DOM currently reflects, as far as `value` goes — lets the `value`-driven effect
    // below tell "the author just typed this" (already live in the DOM; no rebuild needed)
    // apart from "this changed for some other reason" (a fresh compile's unrelated state
    // update, an external reset) that DOES need a rebuild.
    const lastSyncedValueRef = useRef(value)

    const rebuild = () => {
        const root = editorRef.current
        if (!root) return

        const selection = document.getSelection()
        const hasFocus = document.activeElement === root
        let caretOffset: number | null = null
        if (hasFocus && selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            if (root.contains(range.startContainer)) {
                caretOffset = getPlainTextOffset(root, range.startContainer, range.startOffset)
            }
        }

        renderContent(root, value, spans, activeSpanKey)
        lastSyncedValueRef.current = value

        if (caretOffset !== null && selection) {
            const { node, offset } = getPositionAtOffset(root, caretOffset)
            const range = document.createRange()
            range.setStart(node, offset)
            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)
        }
    }

    // Rebuilds whenever the highlight state itself changes (a hover, a click, a fresh compile
    // landing) — these are infrequent compared to keystrokes, so a full rebuild here is cheap
    // and always correct. `value` is deliberately NOT a dependency: it's read fresh via closure
    // (so a rebuild triggered by `spans` still uses current text), but changes to it alone must
    // not retrigger this effect, or every keystroke would rebuild the whole tree.
    useEffect(() => {
        rebuild()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes
        // `value`/`rebuild` (see comment above); `spans` and `activeSpanKey` are the real
        // triggers.
    }, [spans, activeSpanKey])

    // Rebuilds when `value` changes for a reason OTHER than the author's own typing — e.g. the
    // drawer opening on a different directive. A same-value no-op (the common case, since the
    // input handler below already updates `lastSyncedValueRef` before this runs) skips the
    // rebuild entirely, leaving the browser's own live edit and the caret it just placed alone.
    useEffect(() => {
        if (value !== lastSyncedValueRef.current) rebuild()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `rebuild` closes over
        // `spans`/`activeSpanKey` from the same render, which is what we want here too.
    }, [value])

    const syncFromDom = () => {
        const root = editorRef.current
        if (!root) return
        const text = serializePlainText(root)
        lastSyncedValueRef.current = text
        onChange(text)
    }

    const updateActiveSpanFromCaret = () => {
        const root = editorRef.current
        const selection = document.getSelection()
        if (!root || !selection || selection.rangeCount === 0) return
        const range = selection.getRangeAt(0)
        if (!root.contains(range.startContainer)) return

        const caret = getPlainTextOffset(root, range.startContainer, range.startOffset)
        const hit = spans.find((s) => caret >= s.start && caret <= s.end)
        onSpanHover(hit?.key ?? null)
    }

    // Scrolls the requested span's mark into view, centered in the editor's viewport. Reads
    // the mark's own `offsetTop` directly — it's a real child of this same element now, so
    // there's no second layer whose position could ever disagree with it.
    useEffect(() => {
        if (!scrollToSpan) return
        const root = editorRef.current
        const mark = root?.querySelector<HTMLElement>(`mark[data-span-key="${scrollToSpan.key}"]`)
        if (!root || !mark) return

        const target = mark.offsetTop + mark.offsetHeight / 2 - root.clientHeight / 2
        root.scrollTop = Math.max(0, Math.min(target, root.scrollHeight - root.clientHeight))
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the whole
        // {key, nonce} object so the SAME key can be requested again and still re-scroll
    }, [scrollToSpan])

    return (
        <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            data-placeholder={placeholder}
            className={getClassNames('bvd-highlight-editor')}
            onInput={syncFromDom}
            onClick={updateActiveSpanFromCaret}
            onKeyUp={updateActiveSpanFromCaret}
            onKeyDown={(e) => {
                // Left to its default behavior, contentEditable's Enter key in most browsers
                // splits into a new block element (a <div>/<p>) rather than a plain line break
                // — `serializePlainText`/`renderContent` only ever produce text nodes and <br>,
                // so an unhandled Enter would desync the DOM from what this component can
                // read back. `insertLineBreak` inserts a real <br> at the caret and fires the
                // same native `input` event a keystroke would, so `syncFromDom` still runs.
                if (e.key === 'Enter') {
                    e.preventDefault()
                    document.execCommand('insertLineBreak')
                }
            }}
            onPaste={(e) => {
                // Force plain text: pasted HTML (rich formatting, or a whole other document's
                // markup) would otherwise land as real DOM nodes this component's DOM<->text
                // conversion doesn't know how to round-trip.
                e.preventDefault()
                document.execCommand('insertText', false, e.clipboardData.getData('text/plain'))
            }}
        />
    )
}
